import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FormikContext } from "formik";
import PropTypes from "prop-types";
import SelectContainer from "~/components/SelectContainer/SelectContainer";
import { ADD_PROJECT_TEMPLATE_ELEMENT_SUCCESS } from "~/store/action_types";
import { addProjectTemplateElement } from "~/store/actions/actions.projectTemplateElements";
import { makeGetTaskListsByTemplateId } from "~/store/selectors/selector.projectTemplateElements";
import { TemplateTaskPanelItem } from "./TemplateTaskPanelItem";
import { PROJECT_TEMPLATE_RECURRING_TASK, PROJECT_TEMPLATE_TASK_LIST } from "../../utils";

export const TemplateTaskTaskListPicker = ({ task, onChange, fieldRef, type }) => {
  const { t } = useTranslation();
  const formik = useContext(FormikContext);

  const dispatch = useDispatch();

  const getProjectTemplateTaskLists = useMemo(makeGetTaskListsByTemplateId, []);
  const taskLists = useSelector((state) => getProjectTemplateTaskLists(state, task.template_id));

  const taskListOptions = useMemo(() => {
    return taskLists
      .sort((a, b) => (a.position < b.position ? -1 : 1))
      .map((taskList) => ({
        id: taskList.id,
        name: taskList.name,
      }));
  }, [taskLists]);

  const handleChange = useCallback(
    (id) => {
      onChange({ task_list_id: id });
    },
    [onChange]
  );

  const handleEmptyAction = useCallback(
    (value) => {
      const trimmedValue = value.trim();
      if (trimmedValue) {
        dispatch(
          addProjectTemplateElement(task.template_id, { name: trimmedValue, type: PROJECT_TEMPLATE_TASK_LIST })
        ).then((action) => {
          if (action.type === ADD_PROJECT_TEMPLATE_ELEMENT_SUCCESS) {
            handleChange(action.response.result[0]);
          }
        });
      }
    },
    [dispatch, handleChange, task.template_id]
  );

  return (
    <TemplateTaskPanelItem
      label={t("Task List*")}
      invalid={formik?.errors?.task_list_id && formik?.touched?.task_list_id}
    >
      <SelectContainer
        selected={task.task_list_id}
        options={taskListOptions}
        onChange={handleChange}
        className="tw-w-full"
        keepSameOptionsOrder
        fieldRef={(el) => {
          if (fieldRef?.current) return (fieldRef.current.task_list_id = el);
        }}
        invalid={formik?.errors?.task_list_id && formik?.touched?.task_list_id}
        errorMessage={formik?.errors?.task_list_id}
        emptyAction={type === PROJECT_TEMPLATE_RECURRING_TASK ? handleEmptyAction : null}
        emptyValue={type === PROJECT_TEMPLATE_RECURRING_TASK ? t("Add new task list") : null}
      />
    </TemplateTaskPanelItem>
  );
};

TemplateTaskTaskListPicker.propTypes = {
  task: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  fieldRef: PropTypes.object,
  type: PropTypes.string,
};
